import type {Block, getColorForValueOptions, SData, SDataRelatedOptions, SliderPeriod, ValueHumanDescription} from './types';
import ModalColorizerSetupTools from './ModalColorizerSetupTools';
import {smartRounding} from '../../tools/smartRounding';
import {featureGetSmoothNumbers} from "./featureGetSmoothNumber";

interface SliderData {
    data: SData;
    setMinimum(min: number): this;
    setSliderWidth(width: number): this;
    addBlock(max: number, color: string): number;
    getBlocks(withinVisibleMinMax: boolean): Block[];
    getMinimum(): number;
    getMaximum(): number;
    createProportionalBlocks(numberOfBlocks: number, colors: string[]): this;
    setVisibleMinMax(min: number, max: number): this;
    getSliderData(): SData;
    setUseFilter(useFilter: boolean): this;
    getUseFilter(): boolean;
    setTypeName(typeName: string): this;
    getTypeName(): string;
    load(SData: SData): this;
    getSliderPeriods(): SliderPeriod[];
    getBlockLeftPx(index: number): number;
    getBlockBefore(index: number): Block | null;
    setUnit(unit: string): this;
    getUnit(): string;
    getInfinityFrom(): boolean;
    getInfinityTo(): boolean;
    getCalculatedLabelPositions(): number[];
    allowOutsideExtremumValues(allow: boolean): void;
    setRoundValues(round: boolean): void;
    getRoundValues(): boolean;
    removeBlocks(): void;
    setMakeSmoothNumbers(makeSmoothNumbers: boolean): void;
    getMakeSmoothNumbers(): boolean;
}

class SliderData implements SliderData {
    constructor() {
        // console.trace('SliderData.constructor(), setting default data', this.getDefaultSData());
        this.data = this.getDefaultSData();
    }

    setRoundValues(round: boolean) {
        this.data.integers_only = round;
        return this;
    }

    getRoundValues(): boolean {
        return this.data.integers_only;
    }

    getDefaultSData(): SData {
        return {
            use_filter: false,
            block_min: null,
            slider_width_px: 600,
            slider_height_px: 30,
            visible_min: null,
            visible_max: null,
            selected_option: null,
            reversed_color_scale: false,
            unit: '',
            allow_outside_extremum_values: true,
            integers_only: true,
            blocks: [],
            allow_min_max_symbols: true,
            make_smooth_numbers: false,
            related_options: {
                date_first: null,
                date_second: null,
                compare_dates: false,
                related_collection: null,
            }
        }
    }

    setAllowMinMaxSymbols(allow: boolean): this {
        this.data.allow_min_max_symbols = allow;
        return this;
    }

    setInfinityOnBlock(id: number, infinity_type: 'from' | 'to' | ''): this {
        if (this.data.allow_min_max_symbols) {
          this.data.blocks.forEach(block => {
            if (block.infinity_type === infinity_type) {
              block.infinity_type = '';
            }
            if (block.id === id) {
              block.infinity_type = infinity_type;
            }
          });
          this.sortBlocksByMax();
        } else {
          console.warn('Min and max symbols are not allowed');
        }
        return this;
    }

    getInfinityFrom(): boolean {
        return this.data.blocks.some(block => block.infinity_type === 'from');
    }

    getInfinityTo(): boolean {
        return this.data.blocks.some(block => block.infinity_type === 'to');
    }

    load(SData: SData): this {
        // console.log('SliderData.load(SData: SData): this', SData);
        this.data = JSON.parse(JSON.stringify(SData));
        // this.data.slider_width_px = 600; // FIXME: remove this line when slider width is saved in the database
        this.sortBlocksByMax();
        this.calculateLeftPx();
        this.calculateWidthPx();
        this.calculateValueDescriptions();
        return this;
    }

    setTypeName(typeName: string): this {
        this.data.selected_option = typeName;
        return this;
    }

    getTypeName(): string {
        return this.data.selected_option ?? '';
    }

    setUseFilter(useFilter: boolean): this {
        this.data.use_filter = useFilter;
        return this;
    }

    getUseFilter(): boolean {
        return this.data.use_filter;
    }

    getSliderData(): SData {
        return this.data;
    }

    checkDataValues(): boolean {
        if (this.data.block_min === null) {
            console.error('Minimum value must be set before adding blocks. Use setMinimum() for SliderData.');
            return false;
        }
        if (this.data.slider_width_px === 0) {
            console.error('Slider width must be set before adding blocks. Use setSliderWidth() for SliderData.');
            return false;
        }
        if (this.data.visible_max === null) {
            console.error('Visible max value must be set before adding blocks. Use setVisibleMinMax() for SliderData.');
            return false;
        }
        if (this.data.visible_min === null) {
            console.error('Visible min value must be set before adding blocks. Use setVisibleMinMax() for SliderData.');
            return false;
        }
        return true;
    }

    createProportionalBlocks(numberOfBlocks: number, colors: string[] = []): this {
        if (numberOfBlocks <= 0) {
            throw new Error('createProportionalBlocks, Number of blocks must be greater than 0');
        }
        // this.data.slider_width_px = 600; // FIXME: remove this line when slider width is saved in the database
        const min = this.getVisibleMin(); // 200
        const max = this.getVisibleMax(); // 400
        this.setMinimum(min);
        const range: number = max - min; // 200
        if (range <= 0) {
            throw new Error('createProportionalBlocks, Range must be greater than 0');
        }
        const blockRange = range / numberOfBlocks; // 200 / 5 = 40

        this.data.blocks = [];
        for (let i = 0; i < numberOfBlocks; i++) {
            let blockMax: number = min + (i + 1) * blockRange; // 200 + (0 + 1) * 40 = 240
            this.addBlock(blockMax, colors[i] ?? '#000000');
        }
        this.calculateLeftPx();
        this.calculateWidthPx();
        // this.data.make_smooth_numbers = true;
        this.calculateValueDescriptions();
        return this;
    }

    setMinimum(min: string | number): this {
        this.data.block_min = parseFloat(min as unknown as string);
        return this;
    }

    setVisibleMinMax(min: number, max: number, truncate: boolean = false, updateLastBlock: boolean = true): this {
        this.data.visible_min = parseFloat(min as unknown as string);
        this.data.visible_max = parseFloat(max as unknown as string);
        this.calculateLeftPx();
        this.calculateWidthPx();
        this.calculateValueDescriptions();
        if (updateLastBlock) {
            this.updateLastBlockToAvoidSmallerThanVisibleMax();
        }
        if (truncate) {
            this.truncateBlocksToBeWithinVisibleMinMax();
        }
        return this;
    }

    updateLastBlockToAvoidSmallerThanVisibleMax(): this {
        try {
            if (this.getLastBlock().max < this.getVisibleMax()) {
                this.updateBlockMax(this.getLastBlock().id, this.getVisibleMax(), true);
            }
        } catch (e) {
            // console.debug('SliderData.setVisibleMinMax, no blocks yet');
        }
        return this;
    }

    getLastBlock(): Block {
        return this.data.blocks[this.data.blocks.length - 1];
    }

    getVisibleMin(): number {
        return this.data.visible_min ?? 0;
    }

    getVisibleMax(): number {
        return this.data.visible_max ?? 0;
    }

    setSliderWidth(width: number): this {
        this.data.slider_width_px = Math.round(width);
        return this;
    }

    setSliderHeight(height: number): this {
        this.data.slider_height_px = Math.round(height);
        return this;
    }

    getSliderHeight(): number {
        return this.data.slider_height_px;
    }

    addBlock(max: number, color: string): number {
        if (this.getMinimum() === null) {
            throw new Error('Minimum value must be set before adding blocks. Use setMinimum() for SliderData.');
        }
        if (this.data.slider_width_px === 0) {
            throw new Error('Slider width must be set before adding blocks. Use setSliderWidth() for SliderData.');
        }
        if (!this.data.blocks) {
            this.data.blocks = [];
        }
        if (this.data.integers_only) {
            max = Math.round(max);
        }
        let newBlock: Block = {
            id: this.getBlockNextId(),
            max,
            color,
            left_px: null,
            left_input_px: null,
            width_px: null,
            rounded: null,
            infinity_type: ''
        };
        // console.log('addBlock', newBlock);
        this.data.blocks.push(newBlock);
        // console.log('allowExtendRange, data.blocks, before sortBlocksByMax ', this.data.blocks.map( b => b.max) );
        this.sortBlocksByMax();
        // console.log('allowExtendRange, data.blocks, before calculateLeftPx ', this.data.blocks.map( b => b.max));
        this.calculateLeftPx();
        // console.log('allowExtendRange, data.blocks, before calculateWidthPx ', this.data.blocks.map( b => b.max));
        this.calculateWidthPx();
        // console.log('allowExtendRange, data.blocks, before calculateValueDescriptions ', this.data.blocks.map( b => b.max));
        this.calculateValueDescriptions();
        return newBlock.id;
    }

    getBlockLeftPx(index: number): number {
        if (!this.data.blocks) {
            throw new Error('Blocks not defined');
        }
        const block = this.data.blocks.filter(block => block.id === index)[0];
        if (!block) {
            throw new Error(`Block with index ${index} not found`);
        }
        return block.left_px ?? 0;
    }

    getBlockNextId() {
        if (!this.data.blocks) {
            return 0;
        }
        let findMaxIdInBlocks = -1;
        this.data.blocks.forEach(block => {
            if (block.id > findMaxIdInBlocks) {
                findMaxIdInBlocks = block.id;
            }
        });
        return findMaxIdInBlocks + 1;
    }

    updateBlock(index: number, max: number, color: string): this {
        if (typeof max !== 'number') {
            throw new Error('Max must be a number: ' + max);
        }
        if (!this.data.blocks) {
            this.data.blocks = [];
        }
        if (this.data.integers_only) {
            max = Math.round(max);
        }
        // Add new if not exists in blocks
        if (this.data.blocks.filter(block => block.id === index).length === 0) {
            this.data.blocks.push({
                id: this.getBlockNextId(),
                max,
                color,
                left_px: null,
                left_input_px: null,
                width_px: null,
                rounded: null,
                infinity_type: ''
            });
        } else {
            this.data.blocks.forEach(block => {
                if (block.id === index) {
                    block.max = max;
                    if (!this.data.allow_outside_extremum_values) {
                        // console.debug('sliderInside, updateBlock, allow_outside_extremum_values');
                        block.max = Math.min(max, this.getVisibleMax());
                    }
                    block.color = color;
                }
            });
        }

        this.sortBlocksByMax();
        this.calculateLeftPx();
        this.calculateWidthPx();
        this.calculateValueDescriptions();
        return this;
    }

    updateBlockMax(index: number, max: number, keepInSliderRange: boolean = false): this {
        if (!this.data.blocks) {
            this.data.blocks = [];
        }
        if (keepInSliderRange) {
            // console.debug('sliderInside, updateBlockMax');
            max = Math.min(max, this.getVisibleMax());
            max = Math.max(max, this.getVisibleMin());
        }
        if (this.data.integers_only) {
            max = Math.round(max);
            if (keepInSliderRange) {
                // console.debug('sliderInside, updateBlockMax, round_values');
                max = Math.min(max, Math.round(this.getVisibleMax()));
                max = Math.max(max, Math.round(this.getVisibleMin()));
            }
        }
        this.data.blocks.forEach(block => {
            if (block.id === index) {
                if (this.data.allow_outside_extremum_values) {
                    if (max < this.getMinimum()) {
                        max = this.getMinimum();
                    }
                    if (max > this.getMaximum()) {
                        max = this.getMaximum();
                    }
                }
                block.max = max;
            }
        });
        
        // save colors
        let savedColors = this.getBlocks().map(block => block.color);
        this.sortBlocksByMax();
        
        // restore colors
        this.getBlocks().forEach((block, index) => {
            block.color = savedColors[index];
        });
        
        this.calculateLeftPx();
        this.calculateWidthPx();
        this.calculateValueDescriptions();
        return this;
    }

    updateBlockLeftPx(index: number, left_px: number, keepInSliderRange: boolean = false): this {
        if (typeof left_px !== 'number') {
            throw new Error('left_px must be a number ' + left_px);
        }
        if (keepInSliderRange) {
            if (left_px < 0) {
                left_px = 0;
            }
            if (left_px > this.data.slider_width_px) {
                left_px = this.data.slider_width_px;
            }
        }
        this.updateBlockMax(index, this.convertPxToValue(left_px), keepInSliderRange);
        return this;
    }

    calculateValueDescriptions(): void {
        // this.data.make_smooth_numbers = makeSmoothNumbers;
        this.getBlocks().forEach((block, index) => {
            block.rounded = (new smartRounding()).getRoundedValues(block.max, this.getUnit(), { convert_nicer:true });
            if (this.data.make_smooth_numbers) {
                let smoothNumber = featureGetSmoothNumbers.getSmoothNumbers(this.getVisibleMin(), this.getVisibleMax(), this.getSliderWidth(), [block.rounded.rounded_value])[0];
                block.rounded.rounded_value = smoothNumber;
                // console.debug('featureGetSmoothNumbers, ', block.rounded.rounded_value, ' -> ', smoothNumber);
            }
        });
    }

    getLeftPx(value: number, keepInSliderRange: boolean = false): number {
        if (!this.checkDataValues()) return 0;
        const min = this.getVisibleMin();
        const max = this.getVisibleMax();
        const slider_width_px = this.data.slider_width_px;
        if (slider_width_px === 0) {
            throw new Error('slider_width_px is 0');
        }
        const range = max - min;
        const range_to_px_ratio = slider_width_px / range;
        const value_px = (value - min) * range_to_px_ratio;
        if (keepInSliderRange) {
            if (value_px < 0) {
                return 0;
            }
            if (value_px > slider_width_px) {
                return slider_width_px;
            }
        }
        return Math.round(value_px);
    }


    getBlock(id: number): Block {
        if (!this.data.blocks) this.data.blocks = [];
        const block = this.data.blocks.filter(block => block.id === id)[0];
        if (!block) {
            throw new Error(`Block with index ${id} not found`);
        }
        return block;
    }

    ifBlockExists(id: number): boolean {
        if (!this.data.blocks) this.data.blocks = [];
        return this.data.blocks.filter(block => block.id === id).length > 0;
    }

    removeBlock(index: number): this {
        if (!this.data.blocks) this.data.blocks = [];
        this.data.blocks = this.data.blocks.filter(block => block.id !== index);
        this.calculateLeftPx();
        this.calculateWidthPx();
        this.calculateValueDescriptions();
        return this;
    }

    convertPxToValue(px: number): number {
        const min = this.getVisibleMin();
        const max = this.getVisibleMax();
        const slider_width_px = this.data.slider_width_px;
        if (slider_width_px === 0) {
            throw new Error('slider_width_px is 0');
        }
        const range = max - min;
        const px_to_range_ratio = range / slider_width_px;
        const value = min + px * px_to_range_ratio;
        return value;
    }

    calculateWidthPx(): void {
        let width_left_to_assign = this.data.slider_width_px;
        if (width_left_to_assign === 0) {
            throw new Error('slider_width_px is 0');
        }
        if (width_left_to_assign === null) {
            throw new Error('slider_width_px is null');
        }
        this.getBlocks().forEach((block, index) => {
            if (index === 0) {
                block.width_px = Math.max(block.left_px, 0);
            } else {
                const previous_block = this.getBlocks()[index - 1];
                block.width_px = block.left_px - (previous_block.left_px > 0 ? previous_block.left_px : 0);
                if (block.left_px > this.data.slider_width_px) {
                    block.width_px = block.width_px - (block.left_px - this.data.slider_width_px);
                }
            }
            // if (index === this.getBlocks().length - 1 && width_left_to_assign > 0) {
            //     block.width_px = width_left_to_assign;
            // }
            width_left_to_assign = width_left_to_assign - block.width_px;
        });
    }

    calculateLeftPx(): void {
        this.getBlocks().forEach((block) => {
            block.left_px = this.getLeftPx(block.max, true);
            block.left_input_px = this.getLeftPx(block.max, true);
        });
    }

    getBlocks( withinVisibleMinMax: boolean = false): Block[] {
        if (withinVisibleMinMax) {
            return this.data.blocks.filter((block) => {
                return block.max >= this.getVisibleMin() && block.max <= this.getVisibleMax();
            }) || [];
        } else {
            return this.data.blocks || [];
        }
    }
    
    truncateBlocksToBeWithinVisibleMinMax(): void {
        let blocks: Block[] = [];
        this.sortBlocksByMax();
        this.setMinimum(this.getVisibleMin());
        this.getBlocks().forEach((block, index) => {
            // console.debug('truncate, block', block);
            if (block.max <= this.getVisibleMin()) {
                // console.warn('truncate, block.max <= this.getVisibleMin()');
                return;
            }
            if (block.max >= this.getVisibleMin() && block.max <= this.getVisibleMax()) {
                // console.warn('truncate, block.max >= this.getVisibleMin() && block.max <= this.getVisibleMax()', block);
                blocks.push(block);
                return;
            }
            if (this.getBlockBefore(block.id) !== null) {
                // console.warn('truncate, this.getBlockBefore(block.id) !== null', this.getBlockBefore(block.id));
                if (block.max > this.getVisibleMax() && this.getBlockBefore(block.id).max < this.getVisibleMax()) {
                    // console.warn('truncate, block.max > this.getVisibleMax() && this.getBlockBefore(block.id).max < this.getVisibleMax()');
                    block.max = this.getVisibleMax();
                    blocks.push(block);
                    return;
                }
            }
        });
        this.data.blocks = JSON.parse(JSON.stringify(blocks));
        // console.debug(
        //     'updateValuesToBeSmoothLikeLabels, truncateBlocksToBeWithinVisibleMinMax, this.data.blocks', this.data.blocks,
        //     ' min: ', this.getVisibleMin(),
        //     ' max: ', this.getVisibleMax(),
        // );
    }

    getVisibleBlocks(): Block[] {
        // return this.getBlocks(true);
        const blocks: Block[] = [];
        this.getBlocks(true).forEach((block) => {
            blocks.push(block);
        });
        return blocks;
    }
    
    getVisibleRangePx(): [ number, number] {
        let min_px: number = 0;
        let max_px: number = this.getSliderWidth();
        if (this.getBlocks().length === 2) {
            this.sortBlocksByMax();
            let min = this.getBlocks()[0].max;
            let max = this.getBlocks()[1].max;
            if (min < this.getVisibleMin()) {
                min = this.getVisibleMin();
            } else if (min > this.getVisibleMax()) {
                min = this.getVisibleMax();
            }
            if (max < this.getVisibleMin()) {
                max = this.getVisibleMin();
            } else if (max > this.getVisibleMax()) {
                max = this.getVisibleMax();
            }
            min_px = this.convertValueToPx(min);
            max_px = this.convertValueToPx(max);
        } else if (this.getBlocks().length === 1) {
            let max = this.getBlocks()[0].max;
            if (max < this.getVisibleMin()) {
                max = this.getVisibleMin();
            } else if (max > this.getVisibleMax()) {
                max = this.getVisibleMax();
            }
            max_px = this.convertValueToPx(max);
        } else {
            throw new Error('getVisibleBlocksForRange() requires exactly 1 or 2 blocks');
        }
        return [ min_px, max_px ];
    }
    
    getVisibleMediumRangePx(): number {
        let [min, max ]  = this.getVisibleRangePx();
        return (min + max) / 2;
    }

    getMinimum(): number {
        if (!this.checkDataValues()) return 0;
        return this.data.block_min;
    }

    getMaximum(): number {
        if (!this.checkDataValues()) return 0;
        let max = this.getMinimum();
        this.getBlocks().forEach((block) => {
            if (block.max > max) {
                max = block.max;
            }
        });
        // Allow to have value inside slider, even last block is lower
        max = Math.max(max, this.getVisibleMax());
        return max;
    }

    sortBlocksByMax(): void {
        if (this.data.blocks) {
            // Sort by max
            this.data.blocks.sort((a, b) => {
                return (Math.round(a.max*10000) - Math.round(b.max*10000));
                // return (Math.round(a.max) !== Math.round(b.max) ? a.max - b.max : 1);
            });
            // Sort by infinity (infinity is always first or before last (not last))
            this.data.blocks.sort((a, b) => {
                return (Math.round(a.infinity_type === 'to' ? a.max*10000+1 : a.max*10000) - Math.round(b.infinity_type === 'to' ? b.max*10000 + 1 : b.max*10000));
            });
        } else {
            console.warn('No blocks to sort');
        }
    }

    getColors(): string[] {
        const colors: string[] = [];
        this.getBlocks().forEach((block) => {
            colors.push(block.color);
        });
        return colors;
    }

    getBlockByIndex(index: number): Block {
        return this.data.blocks[index];
    }

    setColors(colors: string[]): void {
        colors.forEach((color, index) => {
            this.getBlockByIndex(index).color = color;
        });
    }

    getValues(): number[] {
        const values: number[] = [];
        values.push(this.getMinimum());
        this.getBlocks().forEach((block) => {
            values.push(block.max);
        });
        return values;
    }

    getSliderWidth(): number {
        if (this.data.slider_width_px === null) {
            throw new Error('Slider width must be set before adding blocks. Use setSliderWidth() for SliderData.');
        }
        return this.data.slider_width_px;
    }

    getVisibleBlocksWithoutLast( colorizeMode: boolean = false): Block[] {
        let blocks: Block[] = [];
        this.sortBlocksByMax();
        blocks = JSON.parse(JSON.stringify(this.getBlocks()));
        if (colorizeMode && blocks.length > 0) blocks.pop();
        blocks.forEach((block, index) => {
            if (colorizeMode) {
                if (block.max < this.getVisibleMin() || block.max > this.getVisibleMax()) {
                    blocks.splice(index, 1);
                }
            } else {
                if (block.max< this.getVisibleMin()) {
                    block.left_input_px = 0;
                    block.left_px = 0;
                }
                if (block.max > this.getVisibleMax()) {
                    block.left_input_px = this.getSliderWidth();
                    block.left_px = this.getSliderWidth();
                }
            }
        });
        return blocks;
    }

    //     let periods = [
    //     { min: 100, max: 140, color: '#ff0000', rounded: null },
    //     { min: 140, max: 400, color: '#0000ff', rounded: null },
    // ];
    getSliderPeriods(): SliderPeriod[] {
        const periods: SliderPeriod[] = [];
        let min = this.getVisibleMin();
        this.getVisibleBlocks().forEach((block, index) => {
            let max = block.max;
            if (index === this.getVisibleBlocks().length - 1) {
                max = this.getVisibleMax();
            }
            periods.push({
                min,
                max,
                color: block.color,
                rounded_min: (new smartRounding()).getRoundedValues(min, this.getUnit(), { convert_nicer: true}),
                rounded_max: (new smartRounding()).getRoundedValues(max, this.getUnit(), { convert_nicer: true})
            });
            min = block.max;
        });
        return periods;
    }

    getColorForValue(value: number, options: getColorForValueOptions): string {
        let color = '';
        this.getSliderPeriods().forEach((period) => {
            if ((value > period.min && value < period.max) || color === '') {
                color = period.color;
            }
        });
        if (options && options.withoutHash === true) {
            color = color.replace('#', '');
        }
        return color;
    }

    getMiddleValueOfThisBlock(index: number): number {
        const block = this.getBlock(index);
        const previous_block = this.getBlockBefore(index);
        if (previous_block) {
            return previous_block.max + (block.max - previous_block.max) / 2;
        }
        return this.getVisibleMin() + (block.max - this.getVisibleMin()) / 2;
    }

    getRangePxOfThisBlock(index: number): { left_px: number, right_px: number } {
        const block = this.getBlock(index);
        return { left_px: block.left_px, right_px: block.left_px + block.width_px };
    }

    getRangePxChangeOfThisBlock(index: number): { left_px: number, right_px: number } {
        const block = this.getBlock(index);
        const block_after = this.getBlockAfter(index);
        if (block_after === null) {
            return { left_px: block.left_px, right_px: block.left_px + block.width_px };
        }
        return { left_px: block.left_px, right_px: block_after.left_px + block_after.width_px };
    }

    getBlockBefore(index: number): Block | null {
        let comparingToMax = this.getBlock(index).max;
        let orderedIds = this.getBlocks().filter(block => block.max < comparingToMax).sort((a, b) => b.max - a.max).map(block => block.id);
        if (orderedIds.length > 0) {
            return this.getBlock(orderedIds[0]);
        }
        return null;
    }

    getBlockAfter(index: number): Block | null {
        let comparingToMax = this.getBlock(index).max;
        let orderedIds = this.getBlocks().filter(block => block.max > comparingToMax).sort((a, b) => a.max - b.max).map(block => block.id);
        if (orderedIds.length > 0) {
            return this.getBlock(orderedIds[0]);
        }
        return null;
    }

    convertValueToPx(value: number): number {
        const min = this.getVisibleMin();
        const max = this.getVisibleMax();
        const slider_width_px = this.data.slider_width_px;
        if (slider_width_px === 0) {
            throw new Error('slider_width_px is 0');
        }
        const range = max - min;
        const value_to_range_ratio = range / slider_width_px;
        const px = (value - min) / value_to_range_ratio;
        return px;
    }

    getCalculatedLabelPositions( withoutLast: boolean = false,  desired_distance_between_elements: number = 46): number[] {
        let blocks = this.getVisibleBlocksWithoutLast(withoutLast);
        let element_positions_px =blocks.map(b => b.left_px).slice(0, blocks.length);
        let element_distances_px: number[] = [desired_distance_between_elements];
        if (blocks.length > 1) element_distances_px=Array(blocks.length - 1).fill(desired_distance_between_elements);
        let better_values = (new ModalColorizerSetupTools()).getPlaceValuesBetter(
            0,
            this.getSliderWidth(),
            element_positions_px,
            element_distances_px,
            null
        );
        return better_values;
    }

    calculateLabelPositions( withoutLast: boolean = false ): void {
        let positions = this.getCalculatedLabelPositions(withoutLast);
        this.setLabelPositions(positions);
    }

    setLabelPositions(positions: number[]): void {
        this.data.blocks.forEach((block, index) => {
            if (typeof positions[index] !== 'undefined') {
                block.left_input_px = positions[index];
            } else {
                // throw new Error('Label position not found for block ' + index + ' in positions: ' + positions.join(', '));
                console.warn('Label position not found for block ' + index + ' in positions: ' + positions.join(', '));
            }
        });
    }

    setReversedColorScale(reversed: boolean): void {
        this.data.reversed_color_scale = reversed;
        this.reverseColorScale();
    }

    getReversedColorScale(): boolean {
        return this.data.reversed_color_scale;
    }

    reverseColorScale(): void {
        let colors = this.getColors();
        colors.reverse();
        this.setColors(colors);
    }

    setBlockColor(id: number, color: string): void {
        this.getBlock(id).color = color;
    }

    setUnit(unit: string): this {
        this.data.unit = unit;
        return this;
    }

    getUnit(): string {
        return this.data.unit;
    }

    allowOutsideExtremumValues(allow: boolean): this {
        this.data.allow_outside_extremum_values = allow;
        return this;
    }

    getPeriodValuesForTwoBlocks(block_id_1: number, block_id_2: number): { middle: ValueHumanDescription, padding: ValueHumanDescription, unit: string } {
        this.sortBlocksByMax();
        let block1 = this.getBlock(block_id_1);
        let block2 = this.getBlock(block_id_2);
        return {
            middle: this.getMiddleValueBetweenTwoBlocks(block1, block2),
            padding: this.getPaddingValueBetweenTwoBlocks(block1, block2),
            unit: this.getUnit()
        }
    }

    getMiddleValueBetweenTwoBlocks(block1: Block, block2: Block): ValueHumanDescription {
        let middleValue = block1.max + (block2.max - block1.max) / 2;
        return (new smartRounding()).getRoundedValues(middleValue, this.getUnit(), { convert_nicer: true });
    }

    getPaddingValueBetweenTwoBlocks(block1: Block, block2: Block): ValueHumanDescription {
        let paddingValue = Math.abs((block2.max - block1.max) / 2);
        return (new smartRounding()).getRoundedValues(paddingValue, this.getUnit(), { convert_nicer: true });
    }

    setMiddleValuesForTwoBlocks(block_id_1: number, block_id_2: number, middle: number, padding: number): void {
        
        // console.log('setMiddleValuesForTwoBlocks1', block_id_1, block_id_2, middle, padding);

        // Don`t allow to set middle value outside of visible range
        middle = Math.min(middle, this.getVisibleMax());
        middle = Math.max(middle, this.getVisibleMin());

        // Don`t allow to set padding below 0
        padding = Math.max(0, padding);

        // Don`t allow to move middle (when padding is to big)
        if (middle - padding < this.getVisibleMin()) {
            padding = middle - this.getVisibleMin();
        }

        if (middle + padding > this.getVisibleMax()) {
            padding = this.getVisibleMax() - middle;
        }

        let block1 = this.getBlock(block_id_1);
        let block2 = this.getBlock(block_id_2);
        
        // console.log('setMiddleValuesForTwoBlocks2', block1, block2, middle, padding);
        
        this.updateBlockMax(block1.id, Math.max( this.getVisibleMin(), middle - padding));
        this.updateBlockMax(block2.id, Math.min( this.getVisibleMax(), middle + padding));
    }

    removeBlocks() {
        this.data.blocks = [];
    }
    
    getSmoothRoundedValue(max: number, unit: string): ValueHumanDescription {
        let smoothValue: ValueHumanDescription;
        if (this.data.make_smooth_numbers) {
            smoothValue = (new smartRounding()).getRoundedValues(max, unit, { convert_nicer: true });
            let roundedMin : number = (new smartRounding()).getRoundedValues(this.getVisibleMin(), unit, { convert_nicer: true }).rounded_value;
            let roundedMax : number = (new smartRounding()).getRoundedValues(this.getVisibleMax(), unit, { convert_nicer: true }).rounded_value;
            // let roundedMax : number = this.getVisibleMax() / smoothValue.rounded_divider
            let smoothNumber = featureGetSmoothNumbers.getSmoothNumbers(
                roundedMin,
                roundedMax,
                this.getSliderWidth(),
                [smoothValue.rounded_value]
            );
            // console.debug('getSmoothRoundedValue, ',
            //     smoothValue.rounded_value, ' => ', smoothNumber[0],
            //     ' for min: ', roundedMin,
            //     ' max: ', roundedMax,
            //     ' width: ', this.getSliderWidth()
            // );
            smoothValue.rounded_value = smoothNumber[0];
        } else {
            smoothValue = (new smartRounding()).getRoundedValues(max, unit, { convert_nicer: true });
        }
        return smoothValue;
    }
    
    updateValuesToBeSmoothLikeLabels(): void {
        let blocks = this.getVisibleBlocks();
        // console.debug('updateValuesToBeSmoothLikeLabels 1 ', blocks);
        blocks.forEach((block) => {
            let smoothValue = this.getSmoothRoundedValue(block.max, this.getUnit());
            let v = (new smartRounding()).getOriginalValueFromRounded(
                smoothValue.rounded_value,
                smoothValue.rounded_divider,
                smoothValue.rounded_unit,
                this.getUnit()
            );
            this.updateBlockMax(block.id, v, true);
        });
        // console.debug('updateValuesToBeSmoothLikeLabels 2 ', this.getVisibleBlocks());
        this.truncateBlocksToBeWithinVisibleMinMax();
    }
    
    // restoreValuesToOriginalUnit(): void {
    //     // if (!this.data.make_smooth_numbers) {
    //     //     let blocks = this.getVisibleBlocks();
    //     //     console.debug('updateValuesToBeSmoothLikeLabels 11 ', blocks);
    //     //     blocks.forEach((block) => {
    //     //         let max = (new smartRounding()).getOriginalValueFromRounded(block.max, block.rounded.rounded_divider, block.rounded.rounded_unit, block.rounded.original_unit);
    //     //         this.updateBlockMax(block.id, max, true);
    //     //     });
    //     //     console.debug('updateValuesToBeSmoothLikeLabels 22 ', this.getVisibleBlocks());
    //     //     this.truncateBlocksToBeWithinVisibleMinMax();
    //     // }
    // }
    
    setMakeSmoothNumbers(makeSmoothNumbers: boolean): this {
        this.data.make_smooth_numbers = makeSmoothNumbers;
        return this;
    }

    getMakeSmoothNumbers(): boolean {
        return this.data.make_smooth_numbers;
    }

    setRelatedOptions(relatedOptions: SDataRelatedOptions): this {
        this.data.related_options = relatedOptions;
        return this;
    }

    getRelatedOptions(): SDataRelatedOptions {
        return this.data?.related_options;
    }
    
}

export default SliderData;
